<script setup lang="ts">
/**
 * **主题编辑器** —— 把「调 UI」变成一件几秒钟的事。
 *
 * ## 它解决什么问题
 *
 * 主题系统有五条通道（令牌 / 配方 / 任意 CSS / JS 读回 / 布局），
 * 但那是**给引擎看**的结构。做界面的人想要的是：
 *
 * > 点两下，看到效果；不满意再点两下。
 *
 * 所以这个面板的每一处编辑都走 `registry.beginPreview()` 的**草稿**，
 * 改动立刻落到 `resolvedTheme` 上 → `applyTheme` 立刻重画。
 * 没有「预览」按钮，也不写 localStorage —— 关掉面板草稿就没了。
 *
 * ## ⚠️ 为什么保存一定要「另存为」
 *
 * 内置主题的 id 不允许被覆盖（`saveUserTheme` 会拒绝）。这是刻意的：
 * 覆盖掉之后**没有路回到开箱观感**，而用户想「恢复默认」是很自然的诉求。
 * 所以保存流程是「副本 + 新 id」，并存完自动切过去。
 *
 * ## ⚠️ 未知令牌名不报错，但要**提示**
 *
 * 令牌拼错是主题作者的头号错误，而它的表现是**完全静默**的
 * （CSS 变量写了没人用，什么都不发生）。面板里给一条实时提示，
 * 把「没有对应 CSS 变量」的名字列出来。真正的账由 `verify:theme` 对。
 */
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import type { RecipeSlot, ThemeDocument, ThemeDensity, ThemeMotion } from '@/types/theme'
import { RECIPE_CATALOG, withRecipeDefaults } from '@/theme/recipeCatalog'
import {
  activeThemeId,
  beginPreview,
  endPreview,
  findTheme,
  isBuiltinTheme,
  previewDoc,
  removeUserTheme,
  resolvedTheme,
  saveUserTheme,
  setActiveTheme,
  themeSummaries,
  userThemes,
} from '@/theme/registry'
import { resolvedTokenNames } from '@/theme/tokenNames'
import { downloadThemeFile, parseThemeFile } from '@/theme/themeFile'

/**
 * 深拷贝主题文档。
 *
 * ⚠️ 用 JSON 而不是 `structuredClone`：后者在 Safari 15.4 之前没有，
 * 而这个面板是**用户一定会去点**的地方 —— 在那里抛异常等于主题功能不可用。
 * 主题文档是纯数据（字符串 / 对象 / 数组），JSON 往返是等价的。
 */
function cloneDoc<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T
}


/** 保存用的名字（默认给一个不会撞车的） */
const saveName = ref('')
const notice = ref<{ kind: 'ok' | 'error'; text: string } | null>(null)
const fileInput = ref<HTMLInputElement | null>(null)
const cssDraft = ref('')
const tab = ref<'look' | 'color' | 'css' | 'file'>('look')

/* ── 挂载 → 建立草稿 ─────────────────────────────────────────── */

/**
 * ⚠️ 草稿在**挂载时**建立、在**卸载时**清掉。
 *
 * 它原来跟着一个 `open` prop 走（弹窗的开合）。改成窗口之后，
 * 窗口的开合就是组件的挂载与卸载 —— `NpAreaTree` 用 `v-show` 保状态、
 * 只有 `closeArea` 才真的卸载，所以「关掉窗口 = 放弃未保存的改动」
 * 这条语义没变，而且少了一个需要同步的布尔。
 */
onMounted(() => {
  seedDraftFromActive()
})

onBeforeUnmount(() => {
  /* 关窗口就放弃草稿 —— 否则「关掉再打开」会看到一个没保存的中间状态，
     而用户以为自己什么都没改。 */
  endPreview()
  notice.value = null
})

/**
 * 在「主题库」里选中另一套主题时，草稿要从**新主题**重建。
 *
 * ⚠️ 不重建的话会有个很困惑的表现：草稿还压在 `resolvedTheme` 上面，
 * 于是点哪套主题**界面都不动** —— 看起来像「主题坏了」，
 * 而真相是「有一份草稿盖着」。
 */
watch(activeThemeId, () => {
  seedDraftFromActive()
})

/**
 * 用**当前主题的原始文档**建草稿。
 *
 * ## ⚠️ 这里曾经用 `resolvedTheme.value.doc`，那是个真 bug
 *
 * `resolvedTheme.doc` 是**解析之后**的：它的 `tokens` 已经被展开成
 * 完整令牌表（把派生出来的 `--np-accent` / `--np-cat-*` 全烤了进去）。
 * 而解析的顺序是「**先派生、后显式 tokens 覆盖**」（见 `themeDoc.ts`）——
 * 于是草稿里那些烤进去的 `tokens` 会**盖住**你新改的调色板：
 *
 * ```text
 *   改 palette.accent = #123456
 *     → 派生出 --np-accent: #123456
 *     → 但草稿的 tokens 里有 --np-accent: #f0a04b（旧的，烤进来的）
 *     → 显式覆盖派生 → 界面上**一点变化都没有**
 * ```
 *
 * 症状是「调色板里拖半天没反应」，而它只在**从解析结果建草稿**时出现 ——
 * 也就是说这个 bug 从主题编辑器写出来那天就在，只是没人从这条路径验过。
 *
 * 修法：从 `findTheme(id)` 拿**注册表里那份原始文档**（只含作者写的差异），
 * 派生才不会被盖住。`verify:theme` 里有一对断言把这个陷阱钉住了。
 */
function seedDraftFromActive(): void {
  const raw = findTheme(activeThemeId.value) ?? resolvedTheme.value.doc
  beginPreview({ ...cloneDoc(raw), id: raw.id, name: raw.name })
  cssDraft.value = raw.css ?? ''
  saveName.value = isBuiltinTheme(raw.id) ? `${raw.id}-custom` : `${raw.id}-copy`
}

/** 草稿（面板里所有编辑都写它） */
const draft = computed<ThemeDocument | null>(() => previewDoc.value)

/**
 * 改草稿。
 *
 * ⚠️ 必须**整份替换**（`previewDoc` 是 `shallowRef`）——
 * 原地改属性不会触发重新解析，表现为「拖了取色器没反应」。
 */
function patch(fn: (d: ThemeDocument) => void): void {
  const base = draft.value
  if (!base) return
  const next = cloneDoc(base)
  fn(next)
  beginPreview(next)
}

/* ── 调色板编辑 ─────────────────────────────────────────────── */

const accent = computed({
  get: () => draft.value?.palette?.accent ?? '#f0a04b',
  set: (v: string) =>
    patch((d) => {
      d.palette = { ...(d.palette ?? {}), accent: v }
    }),
})

const danger = computed({
  get: () => draft.value?.palette?.danger ?? '#e05252',
  set: (v: string) =>
    patch((d) => {
      d.palette = { ...(d.palette ?? {}), danger: v }
    }),
})

/** 草稿里显式写了的分类色（只列这些 —— 其余由引擎派生） */
const categoryEntries = computed(() =>
  Object.entries(draft.value?.palette?.categories ?? {}).sort(([a], [b]) => a.localeCompare(b)),
)

function setCategory(id: string, value: string): void {
  patch((d) => {
    d.palette = { ...(d.palette ?? {}), categories: { ...(d.palette?.categories ?? {}), [id]: value } }
  })
}

const socketEntries = computed(() =>
  Object.entries(draft.value?.palette?.sockets ?? {}).sort(([a], [b]) => a.localeCompare(b)),
)

function setSocket(id: string, value: string): void {
  patch((d) => {
    d.palette = { ...(d.palette ?? {}), sockets: { ...(d.palette?.sockets ?? {}), [id]: value } }
  })
}

/* ── 配方 / 密度 ─────────────────────────────────────────────── */

const recipes = computed(() => withRecipeDefaults(draft.value?.recipes))

function setRecipe(slot: RecipeSlot, value: string): void {
  patch((d) => {
    d.recipes = { ...(d.recipes ?? {}), [slot]: value }
  })
}

const density = computed(() => draft.value?.layout?.density ?? 'normal')

function setDensity(v: string): void {
  patch((d) => {
    d.layout = { ...(d.layout ?? {}), density: v as ThemeDensity }
  })
}

/**
 * 动效强度（通道 ⑥）。
 *
 * ⚠️ 它旁边**必须**有一句"系统设置优先"的说明：用户在系统里开了
 * 「减少动态效果」时，这里选什么都不动 —— 不写清楚的话，
 * 他会以为这个开关坏了。这不是我们的选择，是硬要求（晕动症）。
 */
const motion = computed(() => draft.value?.layout?.motion ?? 'normal')

function setMotion(v: string): void {
  patch((d) => {
    d.layout = { ...(d.layout ?? {}), motion: v as ThemeMotion }
  })
}

/* ── CSS ─────────────────────────────────────────────────────── */

function applyCss(): void {
  patch((d) => {
    d.css = cssDraft.value.trim() || undefined
  })
  notice.value = { kind: 'ok', text: '自定义 CSS 已应用（仍被限制在本主题作用域内）' }
}

/* ── 保存 / 导出 / 导入 ──────────────────────────────────────── */

const saveId = computed(() =>
  saveName.value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9._-]+/g, '-')
    .replace(/^-+|-+$/g, ''),
)

function save(): void {
  const d = draft.value
  if (!d) return
  if (!saveId.value) {
    notice.value = { kind: 'error', text: '请先填一个英文 id（只能用小写字母、数字、-、_、.）' }
    return
  }
  const res = saveUserTheme({ ...cloneDoc(d), id: saveId.value, name: saveName.value.trim() })
  if (!res.ok) {
    notice.value = { kind: 'error', text: res.problems.join('；') }
    return
  }
  endPreview()
  setActiveTheme(saveId.value)
  notice.value = { kind: 'ok', text: `已保存并切换到「${saveName.value.trim()}」` }
}

function exportCurrent(): void {
  const d = draft.value
  if (!d) return
  downloadThemeFile({ ...cloneDoc(d), id: saveId.value || d.id }, 'node-print design theme')
}

function pickFile(): void {
  fileInput.value?.click()
}

async function onFile(ev: Event): Promise<void> {
  const input = ev.target as HTMLInputElement
  const file = input.files?.[0]
  input.value = ''
  if (!file) return
  const parsed = parseThemeFile(await file.text())
  if (!parsed.ok) {
    notice.value = { kind: 'error', text: parsed.problems.join('；') }
    return
  }
  const theme = parsed.file.theme
  const { audit } = parsed
  /*
   * ⚠️ 导入**必须在应用之前**把危险的地方说出来。
   * `css` 是一段会被注入页面的样式，`@global` 更是作者主动放弃了作用域保护 ——
   * 默默执行等于替用户做了决定。
   */
  const warnings: string[] = []
  if (audit.hasGlobalCss) warnings.push('含 `@global`（会突破主题作用域，影响整个页面）')
  if (audit.zIndexTokens.length) warnings.push(`改写了层级变量：${audit.zIndexTokens.join('、')}`)
  if (audit.hasCss && !audit.hasGlobalCss) warnings.push('含自定义 CSS（已限制在本主题作用域内）')
  beginPreview(theme)
  cssDraft.value = theme.css ?? ''
  saveName.value = theme.id
  notice.value = {
    kind: 'ok',
    text: `已载入「${theme.name}」（${audit.tokenCount} 个令牌）${warnings.length ? ' —— ' + warnings.join('；') : ''}`,
  }
}

function dropUserTheme(id: string): void {
  if (!removeUserTheme(id)) return
  notice.value = { kind: 'ok', text: `已删除主题 ${id}` }
}

function resetDraft(): void {
  const base = findTheme(activeThemeId.value)
  if (!base) return
  beginPreview({ ...cloneDoc(base), id: base.id, name: base.name })
  cssDraft.value = base.css ?? ''
  notice.value = null
}

/* ── 诊断：草稿里写了但根本没有对应 CSS 变量的令牌名 ───────────── */

const orphanTokens = computed(() => {
  const known = resolvedTokenNames()
  const written = Object.keys(draft.value?.tokens ?? {})
  return written.filter((k) => !known.has(k))
})

const summaryName = computed(() => draft.value?.name ?? '')
</script>

<template>
  <!--
    ⚠️ 这是 **Area 的身体**，不是浮层：没有 `position: fixed`、没有遮罩、
    没有关闭按钮（关窗口是 Area header 上的 ×）。
    它填满父容器并自己滚动 —— `NpArea` 已经把盒子尺寸算好了。
  -->
  <div class="np-theme-editor" role="region" aria-label="主题编辑器">
    <div class="np-theme-editor__tabsbar">
      <span class="np-theme-editor__who">{{ summaryName }}</span>
      <!--
        实时预览是默认行为（每次编辑都重画），所以这里**不需要「预览」按钮**。
        选项卡只是把「改观感 / 改配色 / 写 CSS / 存文件」四件事分开，
        免得一屏塞三十个控件。
      -->
      <nav class="np-theme-editor__tabs">
        <button
          v-for="t in ([
            { id: 'look', label: '观感' },
            { id: 'color', label: '配色' },
            { id: 'css', label: 'CSS' },
            { id: 'file', label: '主题库' },
          ] as const)"
          :key="t.id"
          type="button"
          class="np-theme-editor__tab"
          :class="{ 'is-on': tab === t.id }"
          @click="tab = t.id"
        >
          {{ t.label }}
        </button>
      </nav>
    </div>

    <div class="np-theme-editor__body">
        <!-- ── 观感：配方 + 密度 ─────────────────────────────── -->
        <template v-if="tab === 'look'">
          <label class="np-theme-field">
            <span class="np-theme-field__k">密度</span>
            <select
              class="np-theme-field__v"
              :value="density"
              @change="setDensity(($event.target as HTMLSelectElement).value)"
            >
              <option value="compact">紧凑</option>
              <option value="normal">常规</option>
              <option value="comfortable">宽松</option>
            </select>
          </label>

          <label class="np-theme-field">
            <span class="np-theme-field__k" title="窗口开合、面板滑入、菜单出现的动效强度">动效</span>
            <select
              class="np-theme-field__v"
              :value="motion"
              @change="setMotion(($event.target as HTMLSelectElement).value)"
            >
              <option value="off">关闭</option>
              <option value="subtle">轻量</option>
              <option value="normal">常规</option>
            </select>
          </label>
          <p class="np-theme-editor__note">
            系统开了「减少动态效果」时，这里选什么都不动 —— 那是硬要求，不是偏好。
          </p>

          <label v-for="slot in RECIPE_CATALOG" :key="slot.slot" class="np-theme-field">
            <span class="np-theme-field__k" :title="slot.hint">{{ slot.label }}</span>
            <select
              class="np-theme-field__v"
              :value="recipes[slot.slot]"
              @change="setRecipe(slot.slot, ($event.target as HTMLSelectElement).value)"
            >
              <option v-for="opt in slot.options" :key="opt.value" :value="opt.value">
                {{ opt.label }} —— {{ opt.hint }}
              </option>
            </select>
          </label>
        </template>

        <!-- ── 配色 ─────────────────────────────────────────── -->
        <template v-else-if="tab === 'color'">
          <label class="np-theme-field">
            <span class="np-theme-field__k">强调色</span>
            <input v-model="accent" class="np-theme-field__color" type="color" />
          </label>
          <label class="np-theme-field">
            <span class="np-theme-field__k">危险色</span>
            <input v-model="danger" class="np-theme-field__color" type="color" />
          </label>

          <p class="np-theme-editor__hint">
            下面只列出**当前主题显式写过**的色。没列出的交给引擎派生 ——
            新装的节点包也会自动得到自己的分类色，不会挤在同一个灰里。
          </p>

          <h4 class="np-theme-editor__h">节点头（分类色）</h4>
          <label v-for="[id, color] in categoryEntries" :key="id" class="np-theme-field">
            <span class="np-theme-field__k">{{ id }}</span>
            <input
              class="np-theme-field__color"
              type="color"
              :value="color"
              @input="setCategory(id, ($event.target as HTMLInputElement).value)"
            />
          </label>

          <h4 class="np-theme-editor__h">端口色</h4>
          <p class="np-theme-editor__hint">
            ⚠️ 端口色是**类型约束**的表达：两个不同类型的端口长得一样，
            用户就会以为它们能连。调明暗可以，别让两个类型撞色。
          </p>
          <label v-for="[id, color] in socketEntries" :key="id" class="np-theme-field">
            <span class="np-theme-field__k">{{ id }}</span>
            <input
              class="np-theme-field__color"
              type="color"
              :value="color"
              @input="setSocket(id, ($event.target as HTMLInputElement).value)"
            />
          </label>
        </template>

        <!-- ── CSS 逃生舱 ───────────────────────────────────── -->
        <template v-else-if="tab === 'css'">
          <p class="np-theme-editor__hint">
            任意 CSS。注入时会**自动挂到本主题的作用域**下（前缀
            <code>html[data-np-theme="…"]</code>），所以不会污染宿主页面。
            想突破作用域就在规则前写 <code>@global</code> —— 后果自负。
          </p>
          <textarea
            v-model="cssDraft"
            class="np-theme-editor__code"
            spellcheck="false"
            placeholder=".np-node__header { letter-spacing: 0.4px; }"
          />
          <button class="np-theme-editor__btn" type="button" @click="applyCss">应用</button>
        </template>

        <!-- ── 主题库 / 导入导出 ────────────────────────────── -->
        <template v-else>
          <h4 class="np-theme-editor__h">内置主题</h4>
          <button
            v-for="t in themeSummaries.filter((x) => x.builtin)"
            :key="t.id"
            type="button"
            class="np-theme-card"
            :class="{ 'is-on': activeThemeId === t.id }"
            @click="setActiveTheme(t.id)"
          >
            <span class="np-theme-card__name">{{ t.name }}</span>
            <span class="np-theme-card__hint">{{ t.hint }}</span>
          </button>

          <h4 class="np-theme-editor__h">我的主题（{{ userThemes.length }}）</h4>
          <p v-if="!userThemes.length" class="np-theme-editor__hint">
            还没有。改完点下面的「保存为我的主题」，它会出现在这里、也会出现在「视图 → 主题」菜单里。
          </p>
          <div v-for="t in userThemes" :key="t.id" class="np-theme-card np-theme-card--own">
            <button type="button" class="np-theme-card__pick" @click="setActiveTheme(t.id)">
              <span class="np-theme-card__name">{{ t.name }}</span>
              <span class="np-theme-card__hint">{{ t.id }}</span>
            </button>
            <button
              type="button"
              class="np-theme-editor__icon"
              title="删除"
              @click="dropUserTheme(t.id)"
            >
              <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round">
                <path d="M6 6l12 12M18 6L6 18" />
              </svg>
            </button>
          </div>

          <h4 class="np-theme-editor__h">文件</h4>
          <div class="np-theme-editor__row">
            <button class="np-theme-editor__btn" type="button" @click="pickFile">导入…</button>
            <button class="np-theme-editor__btn" type="button" @click="exportCurrent">导出</button>
            <button class="np-theme-editor__btn" type="button" @click="resetDraft">还原改动</button>
          </div>
          <input
            ref="fileInput"
            class="np-theme-editor__file"
            type="file"
            accept=".json,application/json"
            @change="onFile"
          />
        </template>

        <p v-if="orphanTokens.length" class="np-theme-editor__warn">
          ⚠️ 这些令牌名在样式表里没有对应的 CSS 变量，写了也不会生效：
          {{ orphanTokens.join('、') }}
        </p>
        <p v-if="notice" class="np-theme-editor__notice" :class="`is-${notice.kind}`">
          {{ notice.text }}
        </p>
      </div>

      <footer class="np-theme-editor__foot">
        <input
          v-model="saveName"
          class="np-theme-field__v np-theme-editor__save-name"
          placeholder="我的主题"
          aria-label="主题名称"
        />
        <button class="np-theme-editor__btn is-primary" type="button" @click="save">
          保存为我的主题
        </button>
      </footer>
  </div>
</template>

<style scoped>
/*
 * ⚠️ 这是 **Area 的身体**：填满父容器、自己滚动，**不做定位**。
 *
 * 盒子的尺寸由 `NpArea` 的 main 区算好（它已经是 flex 里的一块），
 * 所以这里只需要 `height: 100%` + `min-height: 0`（后者是 flex 子项
 * 能真正滚动的前提，漏了会看到内容把父容器撑破）。
 *
 * 原来这里是一整套 `position: fixed` + `max-height: min(72vh, 720px)` +
 * 圆角边框投影的浮层样式，全部删掉 —— 那套东西在窗口形态下不但没用，
 * 还会让窗口里多出一个"框中框"。
 *
 * ⚠️ 颜色一律走令牌，不写死 —— 这个组件本身就是用来调主题的，
 * 它自己不能被主题漏掉（否则浅色主题下它会是唯一一块黑）。
 */
.np-theme-editor {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  min-height: 0;
  height: 100%;
  overflow: hidden;
  color: var(--np-panel-text);
  font-size: var(--np-font-size);
}

.np-theme-editor__tabsbar {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 0 0 auto;
  padding: 6px 10px 0;
}

.np-theme-editor__who {
  overflow: hidden;
  color: var(--np-panel-dim);
  white-space: nowrap;
  text-overflow: ellipsis;
}

.np-theme-editor__tabs {
  display: flex;
  gap: 2px;
  margin-left: auto;
}

.np-theme-editor__tab {
  padding: 3px 9px;
  border: 0;
  border-radius: var(--np-radius-s) var(--np-radius-s) 0 0;
  background: transparent;
  color: var(--np-panel-dim);
  font-family: inherit;
  font-size: inherit;
  cursor: pointer;
}

.np-theme-editor__tab.is-on {
  background: var(--np-accent-soft);
  color: var(--np-accent);
}

.np-theme-editor__body {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  gap: 5px;
  min-height: 0;
  padding: 8px 10px;
  overflow: hidden auto;
  overscroll-behavior: contain;
}

/*
 * ⚠️ 宽屏下**限制内容宽度**。
 * 这个窗口可以被拉得很宽（甚至最大化），而设置项排成一行 1200px
 * 会让「标签 —— 控件」隔得看不清对应关系。560px 是实测下来
 * 眼睛不用来回扫的宽度。
 */
.np-theme-editor__body > * {
  max-width: 560px;
}

.np-theme-editor__h {
  margin: 6px 0 1px;
  color: var(--np-panel-dim);
  font-size: var(--np-font-size);
  font-weight: 500;
}

.np-theme-editor__hint {
  margin: 2px 0 4px;
  color: var(--np-panel-mute);
  font-size: 10.5px;
  line-height: 1.5;
}

.np-theme-editor__hint code {
  padding: 0 3px;
  border-radius: 3px;
  background: var(--np-panel-chip-bg);
  font-size: 10px;
}

.np-theme-editor__note {
  margin: -2px 0 4px;
  color: var(--np-panel-mute);
  font-size: 10.5px;
  line-height: 1.6;
}

.np-theme-field {
  display: flex;
  align-items: center;
  gap: 8px;
}

.np-theme-field__k {
  flex: 0 0 84px;
  overflow: hidden;
  color: var(--np-panel-dim);
  white-space: nowrap;
  text-overflow: ellipsis;
}

.np-theme-field__v {
  flex: 1 1 auto;
  min-width: 0;
  height: var(--np-ctl-h-sm);
  padding: 0 4px;
  /*
   * ⚠️⚠️ **这里不能读 `--np-widget-*`。**
   *
   * 那是**节点控件**的令牌，只在 `.np-node` 子树里有浅色覆盖
   * （`.np-theme-light.np-node { --np-widget-bg: #ffffff; … }`）。
   * 主题编辑器在**外壳**里，读到的是 `:root` 的深色默认
   * （`--np-widget-bg: #383838`），于是浅色主题下：
   *
   *   深灰输入框 + 深色文字（`--np-text` 在新版里翻成了 `#1e1e1e`）
   *   → **内容看不清**（用户实测：「纸白的时候是暗色的，内容看不清」）。
   *
   * 改用外壳令牌：它们在 `:root` 和 `:root.np-theme-light` 各有一份，
   * 两种色系下都会翻。
   */
  border: 1px solid var(--np-border-strong);
  border-radius: var(--np-radius-s);
  background: var(--np-surface-raised);
  color: var(--np-panel-text);
  font-family: inherit;
  font-size: 10.5px;
}

.np-theme-field__color {
  flex: 0 0 auto;
  width: 46px;
  height: var(--np-ctl-h-sm);
  padding: 0;
  border: 1px solid var(--np-border-strong);
  border-radius: var(--np-radius-s);
  background: var(--np-surface-raised);
  cursor: pointer;
}

/*
 * CSS 编辑框：窗口形态下可以给得比弹窗大得多 —— 这也是做成窗口的收益之一。
 * 用 `flex` 让它吃掉剩余高度而不是写死，窗口拉高时它跟着长。
 */
.np-theme-editor__code {
  width: 100%;
  flex: 1 1 auto;
  min-height: 160px;
  padding: 6px;
  resize: vertical;
  border: 1px solid var(--np-border-strong);
  border-radius: var(--np-radius-m);
  background: var(--np-panel-input-bg);
  color: var(--np-panel-text);
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 10.5px;
  line-height: 1.5;
}

.np-theme-card {
  display: flex;
  flex-direction: column;
  gap: 1px;
  width: 100%;
  padding: 5px 8px;
  border: 1px solid var(--np-border-soft);
  border-radius: var(--np-radius-m);
  background: transparent;
  color: inherit;
  font-family: inherit;
  font-size: inherit;
  text-align: left;
  cursor: pointer;
}

.np-theme-card:hover {
  background: var(--np-panel-bg-hover);
}

.np-theme-card.is-on {
  border-color: var(--np-accent);
  background: var(--np-accent-soft);
}

.np-theme-card--own {
  flex-direction: row;
  align-items: center;
  gap: 4px;
  padding: 2px 4px 2px 0;
}

.np-theme-card__pick {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  gap: 1px;
  min-width: 0;
  padding: 4px 8px;
  border: 0;
  background: transparent;
  color: inherit;
  font-family: inherit;
  font-size: inherit;
  text-align: left;
  cursor: pointer;
}

.np-theme-card__name {
  color: var(--np-panel-text);
}

.np-theme-card__hint {
  overflow: hidden;
  color: var(--np-panel-mute);
  font-size: 10.5px;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.np-theme-editor__row {
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
}

.np-theme-editor__btn {
  padding: 3px 10px;
  border: 1px solid var(--np-border-soft);
  border-radius: var(--np-radius-m);
  background: var(--np-panel-bg-hover);
  color: var(--np-panel-text);
  font-family: inherit;
  font-size: inherit;
  cursor: pointer;
}

.np-theme-editor__btn:hover {
  border-color: var(--np-border-strong);
}

.np-theme-editor__btn.is-primary {
  border-color: transparent;
  background: var(--np-accent-solid);
  color: var(--np-on-accent);
}

.np-theme-editor__icon {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: var(--np-ctl-h-sm);
  height: var(--np-ctl-h-sm);
  padding: 0;
  border: 0;
  border-radius: var(--np-radius-s);
  background: transparent;
  color: var(--np-panel-dim);
  cursor: pointer;
}

.np-theme-editor__icon:hover {
  background: var(--np-panel-bg-hover);
  color: var(--np-panel-text);
}

.np-theme-editor__file {
  display: none;
}

.np-theme-editor__warn,
.np-theme-editor__notice {
  margin: 4px 0 0;
  padding: 5px 7px;
  border-radius: var(--np-radius-s);
  font-size: 10.5px;
  line-height: 1.5;
}

.np-theme-editor__warn {
  background: var(--np-panel-warn-chip);
  color: var(--np-severity-warning);
}

.np-theme-editor__notice.is-ok {
  background: var(--np-accent-soft);
  color: var(--np-accent);
}

.np-theme-editor__notice.is-error {
  background: var(--np-panel-error-chip);
  color: var(--np-severity-error);
}

/*
 * ⚠️ 底栏**吸底**（`margin-top: auto`），不跟着内容滚走。
 * 窗口可以很高，保存按钮滚出视野之后「改完怎么存」就断了。
 */
.np-theme-editor__foot {
  display: flex;
  gap: 6px;
  flex: 0 0 auto;
  margin-top: auto;
  padding: 8px 10px;
  border-top: 1px solid var(--np-border-soft);
}

.np-theme-editor__save-name {
  flex: 1 1 auto;
  max-width: 260px;
}
</style>
